import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { api, assetUrl } from "@/lib/api";
import { ExploreHolding, ExploreError, ExploreSecondaryLink } from "@/attendee/ExploreShared";
import { UtensilsCrossed, Waves, Compass, BedDouble, Plane, BookOpen, ArrowRight, AlertTriangle } from "lucide-react";

const ICONS = { UtensilsCrossed, Waves, Compass, BedDouble };
const FALLBACK_ICON = { dining: "UtensilsCrossed", pools: "Waves", activities: "Compass", hotel: "BedDouble" };
const FALLBACK_LABEL = { dining: "Dining & Bars", pools: "Pools & Beach", activities: "Activities & Excursions", hotel: "Hotel Essentials" };
const ROUTE = {
  dining: "/app/explore/dining-bars",
  pools: "/app/explore/pools-beach",
  activities: "/app/explore/activities-excursions",
  hotel: "/app/explore/hotel-essentials",
};

export default function Explore() {
  const navigate = useNavigate();
  const { eventId } = useApp();
  const [summary, setSummary] = useState(null);
  const [state, setState] = useState("loading"); // loading | error | ok

  const load = useCallback(() => {
    let active = true;
    setState("loading");
    api.exploreSummary(eventId)
      .then((d) => { if (active) { setSummary(d); setState("ok"); } })
      .catch(() => { if (active) { setState("error"); } });
    return () => { active = false; };
  }, [eventId]);

  useEffect(() => load(), [load]);

  if (state === "loading") {
    return (
      <div className="pt-16 px-4 pb-6" data-testid="explore-loading">
        <div className="pt-4 pb-3">
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Discover</span>
          <h1 className="font-serif text-4xl text-cream leading-tight">Explore</h1>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="att-card aspect-[16/10] animate-pulse" />)}
        </div>
      </div>
    );
  }

  // API/auth failure must NOT look like an empty resort guide.
  if (state === "error") return <ExploreError onRetry={load} />;

  // Safe default: not explicitly activated => holding screen (incl. missing config).
  if (!summary || summary.activated !== true) return <ExploreHolding />;

  const cats = (summary.categories || []).filter((c) => ROUTE[c.key]);
  // Activated but somehow no categories configured — still safer than empty icons.
  if (!cats.length) return <ExploreHolding />;

  return (
    <div className="pt-16 px-4 pb-6">
      <div className="pt-4 pb-3">
        <span className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[hsl(var(--pc-gold))]">Discover</span>
        <h1 className="font-serif text-4xl text-cream leading-tight">Explore</h1>
        <p className="text-sm text-cream-dim mt-1">{summary?.resort_name || "Hyatt Ziva Los Cabos"}</p>
      </div>

      {/* Category grid */}
      <div data-testid="attendee-explore-grid" className="grid grid-cols-2 gap-3">
        {cats.map((c, i) => {
          const Icon = ICONS[c.icon] || ICONS[FALLBACK_ICON[c.key]] || Compass;
          const label = c.label || FALLBACK_LABEL[c.key];
          return (
            <motion.button
              key={c.key}
              data-testid={`attendee-explore-card-${c.key}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.32, delay: i * 0.05 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(ROUTE[c.key])}
              className="att-card overflow-hidden text-left flex flex-col h-full focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"
            >
              {c.image_url ? (
                <div className="w-full aspect-[16/10] overflow-hidden bg-[hsl(var(--pc-surface2))]">
                  <img
                    src={assetUrl(c.image_url)}
                    alt=""
                    width="640"
                    height="400"
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover"
                    style={{ objectPosition: c.image_focal || "center" }}
                  />
                </div>
              ) : (
                <div className="w-full aspect-[16/10] grid place-items-center bg-[hsl(var(--pc-gold)/0.1)]">
                  <Icon className="h-7 w-7 text-[hsl(var(--pc-gold))]" strokeWidth={1.6} />
                </div>
              )}
              <div className="p-3.5">
                <div className="font-serif text-lg leading-tight text-cream">{label}</div>
                {c.subtitle && <div className="text-[11px] text-cream-dim mt-0.5 leading-snug">{c.subtitle}</div>}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* AI imagery disclosure */}
      <p className="mt-3 text-[11px] text-cream-dim text-center leading-snug" data-testid="explore-ai-note">
        Representative AI-generated imagery; actual venues may differ.
      </p>

      {/* Beach safety notice */}
      <button
        onClick={() => navigate(ROUTE.pools)}
        data-testid="explore-beach-notice"
        className="mt-4 w-full att-card p-3.5 flex items-center gap-3 text-left border-l-2 border-[hsl(var(--pc-gold))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--pc-gold))] outline-none"
      >
        <AlertTriangle className="h-5 w-5 text-[hsl(var(--pc-gold))] shrink-0" />
        <div className="min-w-0">
          <div className="text-sm text-cream font-semibold leading-snug">Ocean entry is prohibited at the beach adjacent to the hotel.</div>
          <div className="text-[11px] text-cream-dim mt-0.5">Tap for pool &amp; beach details</div>
        </div>
        <ArrowRight className="h-4 w-4 text-[hsl(var(--pc-gold))] shrink-0 ml-auto" />
      </button>

      {/* Secondary links */}
      <div className="mt-5 space-y-2">
        <div className="text-[11px] uppercase tracking-wide font-semibold text-cream-dim px-1">For your trip</div>
        <ExploreSecondaryLink icon={Plane} title="My Trip" subtitle="Your flights & personal transfers" onClick={() => navigate("/app/trip")} testid="explore-link-mytrip" />
        <ExploreSecondaryLink icon={BookOpen} title="Know Before You Go" subtitle="Travel, entry & guest guidance" onClick={() => navigate("/app/more?tab=kbyg")} testid="explore-link-kbyg" />
      </div>
    </div>
  );
}
